import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { FaEdit, FaCheckCircle } from 'react-icons/fa';
//
import { Subtitle, BodyText, Button, Input, Title } from '../../components/ui';
import Layout from '../../components/Layout';
import Checkbox from '../../components/ui/Checkbox';
import { login } from '../../api/services/customerService';
//
import { setUserInfo } from '../../redux/userInfo';
import { setShowLoading } from '../../redux/systemInfo';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 120vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    padding: 20px 0;
  }

  @media (max-width: 480px) {
    padding: 10px 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 85%;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 95%;
    gap: 15px;
  }

  @media (max-width: 480px) {
    width: 98%;
    gap: 10px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 1px solid #ccc;

  @media (max-width: 768px) {
    flex: none;
  }
`;

const FormContainerTitle = styled.div`
  width: 100%;
  padding: 20px 0;
  justify-content: center;
  align-items: center;
  background-color: #c19330;

  ${Subtitle} {
    color: white;
    font-size: 24px;
  }

  @media (max-width: 768px) {
    ${Subtitle} {
      font-size: 20px;
    }
  }

  @media (max-width: 480px) {
    padding: 15px 0;

    ${Subtitle} {
      font-size: 18px;
    }
  }
`;

const FormContainerBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5% 10%;

  @media (max-width: 768px) {
    padding: 4% 8%;
  }

  @media (max-width: 480px) {
    padding: 3% 5%;
  }
`;

const InputContainer = styled.div`
  width: 100%;
  padding: 5px 5px;
  border: 1px solid #ccc;
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    margin-bottom: 15px;
  }
`;

const LoginFunctionContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    margin-bottom: 15px;
    gap: 10px;
  }
`;

const LoginButton = styled(Button)`
  align-self: center;
  width: 30%;
  padding: 30px;

  @media (max-width: 768px) {
    width: 50%;
    padding: 25px;
  }

  @media (max-width: 480px) {
    width: 80%;
    padding: 20px;
  }
`;

const RegisterButton = styled(Button)`
  align-self: center;
  width: 30%;
  padding: 30px;
  margin-top: 30px;

  @media (max-width: 768px) {
    width: 50%;
    padding: 25px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    width: 80%;
    padding: 20px;
    margin-top: 15px;
  }
`;

const MainTitle = styled(Title)`
  margin: 80px 0 40px 0;
  text-align: center;

  @media (max-width: 768px) {
    margin: 40px 0 30px 0;
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    margin: 20px 0 20px 0;
    font-size: 1rem;
  }
`;

const SubTitle = styled(Title)`
  margin: 0 0 50px 0;
  text-align: center;
  font-size: 0.9em;
  font-weight: normal;
  color: ${(props) => props.theme.colors.secondaryText};

  @media (max-width: 768px) {
    margin: 0 0 30px 0;
    font-size: 0.85em;
  }

  @media (max-width: 480px) {
    margin: 0 0 20px 0;
    font-size: 0.8em;
  }
`;

const NewCustomerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  justify-content: space-between;
  flex: 1;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const NewCustomerText = styled(Subtitle)`
  text-align: center;
  font-weight: normal;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ForgotPasswordLink = styled.a`
  color: ${(props) => props.theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  font-weight: bold;
  background: none;
  border: none;
  padding: 0;
  margin: 0;

  @media (max-width: 768px) {
    align-self: flex-end;
  }

  &:hover {
    color: ${(props) => props.theme.colors.secondary};
  }
`;

const LoginPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [isRemember, setIsRemember] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [emailError, setEmailError] = useState({
    isShow: false,
    msg: '',
  });
  const [pwError, setPwError] = useState({
    isShow: false,
    msg: '',
  });

  const handleLogin = async () => {
    dispatch(setShowLoading({ showLoading: true }));
    const result = await login(email, password);
    dispatch(setShowLoading({ showLoading: false }));
    if (result.is_success) {
      const token = result.user.token;
      localStorage.setItem('token', token);
      dispatch(
        setUserInfo({ ...result.user, loginDt: new Date().toDateString() }),
      );
      navigate('/');
    } else {
      alert(result.message);
    }
  };

  const handleRegister = async () => {
    navigate('/register');
  };

  return (
    <Layout>
      <Container>
        <MainTitle>Sign In or Register</MainTitle>
        <SubTitle>
          Only registered Businesses can access our wholesale catalog and place
          orders online.
          <br />
          Please sign in or create an account to get started.
        </SubTitle>
        <Wrapper>
          <FormContainer>
            <FormContainerTitle>
              <Subtitle align='center'>Returning Customer</Subtitle>
            </FormContainerTitle>
            <FormContainerBody>
              <BodyText weight={'bold'} align={'left'} margin='0 0 10px 0'>
                Email:
              </BodyText>
              <InputContainer>
                <Input
                  type='email'
                  placeholder=''
                  value={email}
                  onChange={setEmail}
                />
              </InputContainer>
              {emailError.isShow ? (
                <BodyText color={'red'}>{emailError.msg}</BodyText>
              ) : null}
              <BodyText weight={'bold'} align={'left'} margin='0 0 10px 0'>
                Password:
              </BodyText>
              <InputContainer>
                <Input
                  type='password'
                  placeholder=''
                  value={password}
                  onChange={setPassword}
                />
              </InputContainer>
              {pwError.isShow ? (
                <BodyText color={'red'}>{pwError.msg}</BodyText>
              ) : null}
              <LoginFunctionContainer>
                <Checkbox
                  checked={isRemember}
                  onChange={() => setIsRemember(!isRemember)}
                  label='Remember me?'
                />
                <ForgotPasswordLink
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot password?
                </ForgotPasswordLink>
              </LoginFunctionContainer>
              <LoginButton onClick={handleLogin}>
                <Subtitle color='white'>LOG IN</Subtitle>
              </LoginButton>
            </FormContainerBody>
          </FormContainer>

          <FormContainer>
            <FormContainerTitle>
              <Subtitle align='center'>New Customer</Subtitle>
            </FormContainerTitle>
            <FormContainerBody>
              <NewCustomerContent>
                <NewCustomerText align='left' weight={'normal'}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ marginRight: '8px', flexShrink: 0 }}>
                      <FaCheckCircle color='#28a745' size={16} />
                    </span>
                    <span>Access the wholesale catalog</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ marginRight: '8px', flexShrink: 0 }}>
                      <FaCheckCircle color='#28a745' size={16} />
                    </span>
                    <span>Faster checkout</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ marginRight: '8px', flexShrink: 0 }}>
                      <FaCheckCircle color='#28a745' size={16} />
                    </span>
                    <span>Quick reorders & favorites lists</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ marginRight: '8px', flexShrink: 0 }}>
                      <FaCheckCircle color='#28a745' size={16} />
                    </span>
                    <span>Track order status and history</span>
                  </div>
                </NewCustomerText>
                <RegisterButton onClick={handleRegister}>
                  <Subtitle color='white'>REGISTER</Subtitle>
                </RegisterButton>
              </NewCustomerContent>
            </FormContainerBody>
          </FormContainer>
        </Wrapper>
      </Container>
    </Layout>
  );
};

export default LoginPage;
